// HomeSignal — the platform-neutral Home/Today composition contract (Phase 1).
// See docs/home/02-homesignal-contract.md. This is a VIEW/composition contract, not a domain source
// of truth: it stores nothing, grants no authorization, and every signal is derived from an
// already-authorized domain read. Ranking is fully deterministic (no LLM) and stable across renders.
//
// Reference implementation — intentionally UNWIRED. No component/BFF/endpoint depends on it yet.

export type HomeSignalPriority =
  | 'CRITICAL' // act now, time-critical (meeting <15m; payment failed)
  | 'ACTION_REQUIRED' // the user must do something (approval; overdue; rejected)
  | 'DUE_SOON' // upcoming with a deadline (meeting today; review tomorrow)
  | 'INFORMATIONAL' // happened, no action (recognition; payslip available)
  | 'ENGAGEMENT'; // culture/optional, only when a live event exists

export type HomeSignalDomain =
  | 'leave' | 'attendance' | 'work' | 'assignment' | 'approval' | 'hr' | 'contract'
  | 'performance' | 'recognition' | 'survey' | 'expense' | 'payroll' | 'payslip'
  | 'loan' | 'benefit' | 'learning' | 'conversation' | 'calendar' | 'meeting'
  | 'document' | 'compliance' | 'music' | 'support' | 'other';

export interface HomeSignalAction {
  /** Localized CTA, e.g. "Review", "Join", "Take survey". */
  label: string;
  /** In-app path, e.g. "/app/hr-actions" — the client tenant-prefixes it. */
  href: string;
  /** true → open externally (e.g. a meeting join_url). */
  external?: boolean;
}

export interface HomeSignal {
  /** STABLE per (domain,type,entity) — the dedup + dismissal key. */
  id: string;
  domain: HomeSignalDomain;
  /** Domain-specific slug, e.g. "leave.approval_pending", "review.due". Also the i18n key. */
  type: string;
  priority: HomeSignalPriority;
  /** Localized short line (or an i18n fallback string). */
  title: string;
  summary?: string;
  action?: HomeSignalAction;
  /** When the signal stands for N items ("3 approvals"). */
  count?: number;
  /** ISO-8601 UTC — when the underlying thing happened. */
  occurredAt?: string;
  /** ISO-8601 UTC — deadline; drives DUE_SOON + overdue escalation. */
  dueAt?: string;
  /** ISO-8601 UTC — client auto-hides after this instant. */
  expiresAt?: string;
  /** Obligations are typically false; awareness/engagement true. */
  dismissible: boolean;
  /** Source entity (e.g. {type:"payroll_run", id:"123"}) — dedup + deep-routing. */
  entity?: { type: string; id: string };
  /** Client renders minimally (money masked, no case body). */
  sensitive?: boolean;
}

export interface HomeSource {
  domain: HomeSignalDomain;
  /** ok = fetched; unavailable = call failed (degrade, don't fail); skipped = not authorized/entitled. */
  status: 'ok' | 'unavailable' | 'skipped';
}

export interface HomeTodayResponse {
  generatedAt: string;
  signals: HomeSignal[];
  sources: HomeSource[];
}

/** Tier order, highest first. Index = rank weight (lower = higher priority). */
export const PRIORITY_ORDER: HomeSignalPriority[] = [
  'CRITICAL', 'ACTION_REQUIRED', 'DUE_SOON', 'INFORMATIONAL', 'ENGAGEMENT',
];

const tierIndex = (p: HomeSignalPriority): number => {
  const i = PRIORITY_ORDER.indexOf(p);
  return i === -1 ? PRIORITY_ORDER.length : i;
};

const ms = (iso?: string): number => {
  if (!iso) return NaN;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? NaN : t;
};

/** A signal is overdue when it has a past dueAt and still expects action. */
export const isOverdue = (s: HomeSignal, now = Date.now()): boolean => {
  const d = ms(s.dueAt);
  return !Number.isNaN(d) && d < now && (s.priority === 'ACTION_REQUIRED' || s.priority === 'DUE_SOON');
};

// effectiveTier: an overdue ACTION_REQUIRED/DUE_SOON escalates to the top of the ACTION_REQUIRED band
// (an overdue thing outranks a not-yet-due one), without leaving its tier's floor above CRITICAL.
const effectiveTier = (s: HomeSignal, now: number): number => {
  const base = tierIndex(s.priority);
  if (isOverdue(s, now)) {
    // place just below CRITICAL: use ACTION_REQUIRED tier minus a hair so overdue leads that band.
    return Math.min(base, tierIndex('ACTION_REQUIRED')) - 0.5;
  }
  return base;
};

/**
 * Deterministic comparator (see contract §3): tier (overdue-escalated) → dueAt asc (missing last)
 * → occurredAt desc → id. Total order, stable across renders. Returns <0 if a ranks before b.
 */
export function compareSignals(a: HomeSignal, b: HomeSignal, now = Date.now()): number {
  const ta = effectiveTier(a, now);
  const tb = effectiveTier(b, now);
  if (ta !== tb) return ta - tb;

  const da = ms(a.dueAt);
  const db = ms(b.dueAt);
  const aHas = !Number.isNaN(da);
  const bHas = !Number.isNaN(db);
  if (aHas && bHas && da !== db) return da - db; // soonest deadline first
  if (aHas !== bHas) return aHas ? -1 : 1; // a signal with a deadline outranks one without

  const oa = ms(a.occurredAt);
  const ob = ms(b.occurredAt);
  const oaHas = !Number.isNaN(oa);
  const obHas = !Number.isNaN(ob);
  if (oaHas && obHas && oa !== ob) return ob - oa; // newest first
  if (oaHas !== obHas) return oaHas ? -1 : 1;

  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0; // stable tiebreak
}

/** A signal is visible when not expired and not dismissed (obligations ignore dismissal). */
export function isVisible(s: HomeSignal, dismissed: ReadonlySet<string>, now = Date.now()): boolean {
  const exp = ms(s.expiresAt);
  if (!Number.isNaN(exp) && exp <= now) return false;
  if (s.dismissible && dismissed.has(s.id)) return false;
  return true;
}

/**
 * rankSignals: dedup by id (most-recent wins), drop expired/dismissed, sort deterministically, cap.
 * Pure — inject `now` for testability. `cap` omitted = no cap.
 */
export function rankSignals(
  signals: HomeSignal[],
  opts: { dismissed?: ReadonlySet<string>; now?: number; cap?: number } = {},
): HomeSignal[] {
  const now = opts.now ?? Date.now();
  const dismissed = opts.dismissed ?? new Set<string>();

  // Dedup by id, keeping the most recent occurrence.
  const byId = new Map<string, HomeSignal>();
  for (const s of signals) {
    const prev = byId.get(s.id);
    if (!prev) {
      byId.set(s.id, s);
      continue;
    }
    const sNewer = (ms(s.occurredAt) || 0) >= (ms(prev.occurredAt) || 0);
    if (sNewer) byId.set(s.id, s);
  }

  const ranked = Array.from(byId.values())
    .filter((s) => isVisible(s, dismissed, now))
    .sort((a, b) => compareSignals(a, b, now));

  return opts.cap != null ? ranked.slice(0, opts.cap) : ranked;
}
